import { get, omitBy } from "lodash/fp";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Checkbox,
  Container,
  Content,
  FlexboxGrid,
  Footer,
  Header,
  Pagination,
  Table,
  Input,
  InputGroup,
} from "rsuite";
import SearchIcon from "@rsuite/icons/Search";
import CloseIcon from "@rsuite/icons/Close";
import { OrderServices } from "../../../core/services/OrderServices";
import { UtilityServices } from "../../../core/services/UtilityServices";
import { AlertModal } from "../../components/Modals/AlertModal/AlertModal";
import { OrderFields, OrderListParams, Permission } from "./OrderDataTypes";
import "./Orders.css";
import OrderFilters from "./OrderFilters";
import {
  displayDayDateField,
  displayTimeField,
  isEmptyVal,
  isNotEmpty,
  isWebResolutionSize,
} from "../../../utils/helpers";
import ImageCellFormatter from "../../components/Elements/ImageCellFormatter";
import CustomerCell from "../../components/Elements/CustomerCell";
import OrderItemsCell from "../../components/Elements/OrderItemsCell";
import OrderAmountCell from "../../components/Elements/OrderAmountCell";
import OrderServiceRatingCell from "../../components/Elements/OrderServiceRatingCell";
import OrderAssignedServedCell from "../../components/Elements/OrderAssignedServedCell";
import CustomCell from "../../components/Elements/CustomCell";
import CustomHeaderCell from "../../components/Elements/CustomHeaderCell";
import CheckCell from "../../components/Elements/CheckCell";
import OrderSingleAction from "./OrderSingleAction";
import OrderBulkAction from "./OrderBulkAction";
import { ContentRoutes } from "../../../navigation/NavConstant";
import { ToastContainer } from "react-toastify";
import ImageModal from "../../components/Modals/ImageModal/ImageModal";
import {
  orderDayTypeObj,
  statusArr,
  messageTypeArr,
  appConstObj,
  roleTypeArr,
} from "../../../core/config/AppConstants";
import { useTranslation } from "react-i18next";
import { displayField } from "../../../utils/helpers";
import PhoneCell from "../../components/Elements/PhoneCell";
import OrderNoteCell from "../../components/Elements/OrderNoteCell";

interface ImageModal {
  openModal: () => void;
  removePic: () => void;
  hideDelete: () => void;
}

function OrdersTab() {
  const { t } = useTranslation();
  const { Cell, Column } = Table;
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState(appConstObj.record_per_page);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<OrderFields[]>([]);
  const [sort_by, setSortBy] = useState<any>();
  const [sort_order, setSortOrder] = useState<any>();
  const company = useSelector((state: any) => state.company);
  const modalRef = useRef<ImageModal>(null);
  const [selectedOrder, setSelectedOrderData] = useState<any>({});
  const [openPictureDelModal, setOpenPictureDelModal] = useState(false);
  const [openOrderValueModal, setOpenOrderValueModal] = useState(false);
  const [orderValue, setOrderValue] = useState(0);

  const [smartSearchInput, setSmartSearch] = useState<string>("");
  const [isWebResolution, setIsWebResolution] = useState(isWebResolutionSize());
  const loggedInUserDetail = JSON.parse(
    localStorage.getItem("loggedInUserDetail") || "{}",
  );
  const [tabView, setTabView] = useState(orderDayTypeObj.today.value);

  const initSearchFormData = {
    order_no: "",
    item_name: "",
    customer: "",
    customer_name: "",
    customer_phone: "",
    company: "",
    store: "",
    order_serve_at: "",
    order_assigned_to: "",
    date_interval_type: tabView,
    service_date_from: null,
    service_date_to: null,
    service_date: "",
    service_starts_at: "",
    served_by: "",
    order_status: "",
    is_order_rescheduled: "",
    rating_feedback_written_by: "",
    order_value: "",
    order_value_op: "",
    discount_percent: "",
    discount_percent_op: "",
    service_rating: "",
    service_rating_op: "",
    building: "",
    area: "",
    city: "",
    state: "",
    country: "",
    pincode: "",
    status: "",
    smart_search: "",
  };

  const [formData, setFormData] = useState<OrderListParams>(initSearchFormData);
  const [showCreate, setShowCreate] = useState<boolean>();
  const navigate = useNavigate();
  const [checkedKeys, setCheckedKeys] = React.useState<number[]>([]);
  let checked = false;
  let indeterminate = false;
  const [failedImages, setFailedImages] = useState<any>([]);
  const publishPermission = UtilityServices.hasPermission("update-order");
  const servePermission = UtilityServices.hasPermission("update-order");
  const assignPermission = UtilityServices.hasPermission("update-order");
  const updatePermission = UtilityServices.hasPermission("update-order");
  const deletePermission = UtilityServices.hasPermission("delete-order");
  const serviceRatingPermission =
    UtilityServices.hasPermission("service-rating");
  const permissionObj: Permission = {
    cancel_restore: publishPermission,
    update: updatePermission,
    delete: deletePermission,
    service_rating: serviceRatingPermission,
    serve: servePermission,
    assign: assignPermission,
  };

  if (checkedKeys.length === data.length) {
    checked = true;
  } else if (checkedKeys.length === 0) {
    checked = false;
  } else if (checkedKeys.length > 0 && checkedKeys.length < data.length) {
    indeterminate = true;
  }

  const handleCheckAll = (value: any, checked: any) => {
    const keys = checked ? data.map((item) => item.id) : [];
    setCheckedKeys(keys);
  };

  const handleCheck = (value: number, checked: any) => {
    const keys = checked
      ? [...checkedKeys, value]
      : checkedKeys.filter((item) => item !== value);
    setCheckedKeys(keys);
  };

  const updateResolution = () => {
    setIsWebResolution(isWebResolutionSize());
  };

  async function getOrders(params: OrderListParams) {
    try {
      setLoading(true);
      params["date_interval_type"] = tabView;
      const res = await OrderServices.getOrders(params);
      setData(get("data.data", res));
      setTotal(get("data.total", res));
      setCheckedKeys([]);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }

  async function init() {
    try {
      setShowCreate(UtilityServices.hasPermission("create-order"));
    } catch (error) {
      console.log(error);
    }
  }

  function processCreateOrder() {
    if (
      UtilityServices.getLoggedInUser("user_type") == roleTypeArr.backend &&
      !company.id
    ) {
      UtilityServices.showToaster({
        message: t("REQUIRED_SELECT", {
          field: t("COMPANY").toLowerCase(),
        }),
        type: messageTypeArr.error,
      });
      return;
    }
    navigate(ContentRoutes.CREATE_ORDER);
  }

  useEffect(() => {
    window.addEventListener("resize", updateResolution);
    init();
  }, []);

  async function deleteOrder(order: any) {
    try {
      let res = await OrderServices.deleteOrder(order?.current?.id);
      setPage(1);
      getOrders(
        getSearchParams({
          date_interval_type: tabView,
          page: 1,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: rows,
        }),
      );
      UtilityServices.showToaster({
        message: res.data.message,
      });
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  async function cancelRestoreOrder(orderId: number, payload: any) {
    try {
      const res: any = await OrderServices.cancelRestoreOrder(orderId, payload);
      setPage(1);
      getOrders(
        getSearchParams({
          date_interval_type: tabView,
          page: 1,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: rows,
        }),
      );
      UtilityServices.showToaster({
        message: res?.data?.message,
      });
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error?.message,
        type: messageTypeArr.error,
      });
    }
  }

  async function serveOrder(orderId: number, payload: any) {
    try {
      const res: any = await OrderServices.serveOrder(orderId, payload);
      setPage(1);
      getOrders(
        getSearchParams({
          date_interval_type: tabView,
          page: 1,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: rows,
        }),
      );
      UtilityServices.showToaster({
        message: res?.data?.message,
      });
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error?.message,
        type: messageTypeArr.error,
      });
    }
  }

  async function bulkDeleteAction() {
    try {
      let res = await OrderServices.deleteBulkOrder(checkedKeys);
      setPage(1);
      getOrders(
        getSearchParams({
          date_interval_type: tabView,
          page: 1,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: rows,
        }),
      );
      UtilityServices.showToaster({
        message: res.data.message,
      });
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  async function bulkAssignAction(payload: any) {
    try {
      let res = await OrderServices.assignBulkOrder(checkedKeys, payload);
      setPage(1);
      getOrders(
        getSearchParams({
          date_interval_type: tabView,
          page: 1,
          sort_by: sort_by,
          sort_order: sort_order,
          rows: rows,
        }),
      );
      UtilityServices.showToaster({
        message: res.data.message,
      });
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  async function exportOrderAction() {
    try {
      await OrderServices.exportOrders(getSearchParams());
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  async function valueOrderAction() {
    try {
      let res = await OrderServices.getOrderTotalValue(getSearchParams());
      setOrderValue(res?.data?.data);
      setOpenOrderValueModal(true);
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  function getFailedImages(val: any) {
    setFailedImages(val);
  }

  function customerPicModal(value: any) {
    if (
      !failedImages.includes(value.customer_large_image_url) &&
      value.customer_large_image_url != null &&
      value.customer_large_image_url !== ""
    ) {
      setSelectedOrderData(value);
      modalRef.current?.openModal();
      modalRef.current?.hideDelete();
    }
  }

  function getSearchParams(data?: any) {
    let paramsData = {
      ...formData,
      ...{ smart_search: smartSearchInput },
      ...{ sort_by, sort_order },
      ...{ company: getCompanyId() },
      ...data,
    };
    let params: OrderListParams = omitBy(isEmptyVal, paramsData);
    params["date_interval_type"] = tabView;
    if (data?.page) {
      params.page = data?.page;
    }
    return params;
  }

  function getCompanyId() {
    let compId = null;
    if (UtilityServices.getLoggedInUser("user_type") === roleTypeArr.client) {
      compId = UtilityServices.getLoggedInUser("company_id");
    } else if (company?.id) {
      compId = company.id;
    }
    return compId;
  }

  function onPageChange(page: number) {
    setPage(page);
    getOrders(
      getSearchParams({
        date_interval_type: tabView,
        page: page,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      }),
    );
  }

  function onSortChange(dataKey: any, sortType: any) {
    setPage(1);
    setSortBy(dataKey);
    setSortOrder(sortType);
    getOrders(
      getSearchParams({
        date_interval_type: tabView,
        page: 1,
        sort_by: dataKey,
        sort_order: sortType,
        rows: rows,
      }),
    );
  }

  function submitSearch(formData: any) {
    setFormData(formData);
    getOrders(getSearchParams(formData));
  }

  function clearSearch() {
    setSmartSearch("");
    setFormData(initSearchFormData);
    setSortBy(null);
    setSortOrder(null);
    setPage(1);
    setRows(appConstObj.record_per_page);
    getOrders(
      getSearchParams({
        ...initSearchFormData,
        ...{
          date_interval_type: tabView,
          page: 1,
          rows: appConstObj.record_per_page,
        },
      }),
    );
  }

  const filterOrders = (e: any) => {
    if (e.key === "Enter" || e.type === "click") {
      getOrders(
        getSearchParams({
          smart_search: smartSearchInput,
          rows: rows,
          date_interval_type: tabView,
        }),
      );
    }
  };

  const clearSmartSearch = () => {
    setSmartSearch("");
    setSortBy(null);
    setSortOrder(null);
    setPage(1);
    setRows(appConstObj.record_per_page);
    getOrders(
      getSearchParams({
        date_interval_type: tabView,
        smart_search: "",
        page: 1,
        sort_by: null,
        sort_order: null,
        rows: appConstObj.record_per_page,
      }),
    );
  };

  const handleChangeRows = (rows: any) => {
    setRows(rows);
    setPage(1);
    getOrders(
      getSearchParams({
        date_interval_type: tabView,
        page: 1,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      }),
    );
  };

  const [showColumns, setShowColumns] = useState([
    { fieldName: t("IMAGE") },
    { fieldName: t("CUSTOMER_NAME") },
    { fieldName: t("PHONE") },
    { fieldName: t("TIME") },
    { fieldName: t("ITEMS") },
    { fieldName: t("NOTE") },
    { fieldName: t("AMOUNT") },
    { fieldName: t("TAX") },
    { fieldName: t("ORDER_STATUS") },
    { fieldName: t("SERVICE_RATING") },
    { fieldName: t("SERVICE_BY") },
    { fieldName: t("ORDER_SERVE_AT") },
    { fieldName: t("STORE") },
    { fieldName: t("COMPANY") },
  ]);

  useEffect(() => {
    getOrders(
      getSearchParams({
        date_interval_type: tabView,
        page: 1,
        sort_by: sort_by,
        sort_order: sort_order,
        rows: rows,
      }),
    );
    switch (tabView) {
      case orderDayTypeObj.today.value:
        setShowColumns([
          { fieldName: t("IMAGE") },
          { fieldName: t("CUSTOMER_NAME") },
          { fieldName: t("PHONE") },
          { fieldName: t("TIME") },
          { fieldName: t("ITEMS") },
          { fieldName: t("NOTE") },
          { fieldName: t("AMOUNT") },
          { fieldName: t("TAX") },
          { fieldName: t("ORDER_STATUS") },
          { fieldName: t("SERVICE_RATING") },
          { fieldName: t("SERVICE_BY") },
          { fieldName: t("ORDER_SERVE_AT") },
          { fieldName: t("STORE") },
          { fieldName: t("COMPANY") },
        ]);
        break;
      case orderDayTypeObj.upcoming.value:
        setShowColumns([
          { fieldName: t("IMAGE") },
          { fieldName: t("CUSTOMER_NAME") },
          { fieldName: t("PHONE") },
          { fieldName: t("DATE") },
          { fieldName: t("TIME") },
          { fieldName: t("ITEMS") },
          { fieldName: t("AMOUNT") },
          { fieldName: t("TAX") },
          { fieldName: t("ORDER_STATUS") },
          { fieldName: t("SERVICE_RATING") },
          { fieldName: t("SERVICE_BY") },
          { fieldName: t("ORDER_SERVE_AT") },
          { fieldName: t("STORE") },
          { fieldName: t("COMPANY") },
        ]);
        break;
      case orderDayTypeObj.past.value:
      case orderDayTypeObj.all.value:
        setShowColumns([
          { fieldName: t("IMAGE") },
          { fieldName: t("CUSTOMER_NAME") },
          { fieldName: t("PHONE") },
          { fieldName: t("DATE") },
          { fieldName: t("TIME") },
          { fieldName: t("ITEMS") },
          { fieldName: t("AMOUNT") },
          { fieldName: t("TAX") },
          { fieldName: t("ORDER_STATUS") },
          { fieldName: t("SERVICE_RATING") },
          { fieldName: t("SERVICE_BY") },
          { fieldName: t("ORDER_SERVE_AT") },
          { fieldName: t("STORE") },
          { fieldName: t("COMPANY") },
        ]);
        break;
      default:
        break;
    }
  }, [tabView, company]);

  return (
    <div className="orders">
      <Container className="container">
        <Header className="header">
          <FlexboxGrid justify="space-between">
            <FlexboxGrid.Item colspan={8}>
              <h4>{t("ORDERS")}</h4>
            </FlexboxGrid.Item>
            {showCreate && (
              <FlexboxGrid.Item colspan={4} style={{ textAlign: "end" }}>
                <Button appearance="primary" onClick={processCreateOrder}>
                  {t("CREATE")}
                </Button>
              </FlexboxGrid.Item>
            )}
          </FlexboxGrid>
        </Header>
        <Content>
          <OrderFilters
            tabViewSelected={tabView}
            onSubmit={submitSearch}
            onClear={clearSearch}
          />
          <div className="row">
            <OrderBulkAction
              checkedKeys={checkedKeys}
              setCheckedKeys={setCheckedKeys}
              onBulkDelete={bulkDeleteAction}
              onBulkAssign={bulkAssignAction}
              onExportOrder={exportOrderAction}
              onValueOrder={valueOrderAction}
            />
            <div className="col-md-4">
              <InputGroup>
                <Input
                  className="form-control"
                  placeholder={t("SMART_SEARCH_BY", {
                    entity:
                      t("CUSTOMER").toLowerCase() +
                      " " +
                      t("NAME").toLowerCase() +
                      ", " +
                      t("PHONE").toLowerCase() +
                      ", " +
                      t("OR").toLowerCase() +
                      " " +
                      t("ORDER_NO").toLowerCase(),
                  })}
                  value={smartSearchInput}
                  onChange={(val: any) => setSmartSearch(val)}
                  onKeyDown={(e: any) => filterOrders(e)}
                />
                <InputGroup.Button
                  title={t("SEARCH")}
                  onClick={(e: any) => filterOrders(e)}
                >
                  <SearchIcon />
                </InputGroup.Button>
                <InputGroup.Button
                  title={t("CLEAR")}
                  onClick={() => clearSmartSearch()}
                >
                  <CloseIcon />
                </InputGroup.Button>
              </InputGroup>
            </div>
          </div>
          <div>
            <button
              style={{
                opacity: tabView === orderDayTypeObj.today.value ? 1 : 0.5,
              }}
              className="tab-button"
              onClick={(e) => setTabView(orderDayTypeObj.today.value)}
              key={"today"}
            >
              {t("TODAY")}
            </button>
            <button
              style={{
                opacity: tabView === orderDayTypeObj.upcoming.value ? 1 : 0.5,
              }}
              className="tab-button"
              onClick={(e) => setTabView(orderDayTypeObj.upcoming.value)}
              key={"upcoming"}
            >
              {t("UPCOMING")}
            </button>
            <button
              style={{
                opacity: tabView === orderDayTypeObj.past.value ? 1 : 0.5,
              }}
              className="tab-button"
              onClick={(e) => setTabView(orderDayTypeObj.past.value)}
              key={"past"}
            >
              {t("PAST")}
            </button>
            <button
              style={{
                opacity: tabView === orderDayTypeObj.all.value ? 1 : 0.5,
              }}
              className="tab-button"
              onClick={(e) => setTabView(orderDayTypeObj.all.value)}
              key={"all"}
            >
              {t("ALL")}
            </button>
          </div>
          <div className={total > 0 ? "usr-tbl-border" : "no-record-table"}>
            <Table
              height={400}
              fillHeight={true}
              data={data}
              loading={loading}
              onSortColumn={onSortChange}
              sortColumn={sort_by}
              bordered={true}
              cellBordered
              sortType={sort_order}
              hover={true}
            >
              {isWebResolution && (
                <Column width={50} align="center" fixed>
                  <CustomHeaderCell>
                    <div className="mb-hide">
                      <Checkbox
                        inline
                        checked={checked}
                        indeterminate={indeterminate}
                        onChange={handleCheckAll}
                      />
                    </div>
                  </CustomHeaderCell>
                  <CheckCell
                    dataKey="id"
                    checkedKeys={checkedKeys}
                    onChange={handleCheck}
                  />
                </Column>
              )}
              {showColumns?.length &&
                showColumns.map((field: any, index: number) => {
                  return (
                    <>
                      {field.fieldName === t("IMAGE") ? (
                        <Column
                          width={50}
                          align="center"
                          fixed={isWebResolution ? true : false}
                          key={index + "image"}
                        >
                          <CustomHeaderCell>{t("IMAGE")}</CustomHeaderCell>
                          <ImageCellFormatter
                            dataKey="customer_large_image_url"
                            rowData={(rowData: any) => rowData}
                            dummyImage={UtilityServices.getLoggedInUser(
                              "user_default_image",
                            )}
                            onFailedImages={getFailedImages}
                            openPictureModal={customerPicModal}
                          />
                        </Column>
                      ) : null}

                      {field.fieldName === t("CUSTOMER_NAME") ? (
                        <Column
                          width={140}
                          sortable
                          resizable
                          fixed={isWebResolution ? true : false}
                          key={index + "cust_name"}
                        >
                          <CustomHeaderCell>
                            {t("CUSTOMER_NAME")}
                          </CustomHeaderCell>
                          <CustomerCell
                            rowData={(rowData: any) => rowData}
                            dataKey="customer_name"
                          />
                        </Column>
                      ) : null}

                      {field.fieldName === t("PHONE") ? (
                        <Column
                          width={110}
                          resizable
                          fixed={isWebResolution ? true : false}
                          key={index + "phone"}
                        >
                          <CustomHeaderCell>{t("PHONE")}</CustomHeaderCell>

                          <PhoneCell
                            positionFromRight="60%"
                            phoneIndex="customer_phone"
                            rowData={(rowData: any) => rowData}
                          />
                        </Column>
                      ) : null}
                      {field.fieldName === t("DATE") ? (
                        <Column
                          width={120}
                          sortable
                          resizable
                          key={index + "date"}
                        >
                          <CustomHeaderCell>{t("DATE")}</CustomHeaderCell>
                          <CustomCell dataKey="service_date">
                            {(rowData: any) => (
                              <span>
                                {displayDayDateField(rowData.service_date)}
                              </span>
                            )}
                          </CustomCell>
                        </Column>
                      ) : null}

                      {field.fieldName === t("TIME") ? (
                        <Column width={70} sortable key={index + "time"}>
                          <CustomHeaderCell>{t("TIME")}</CustomHeaderCell>
                          <CustomCell dataKey="service_starts_at">
                            {(rowData: any) => (
                              <span>
                                {isNotEmpty(rowData.service_starts_at)
                                  ? displayTimeField(
                                      rowData.service_date +
                                        " " +
                                        rowData.service_starts_at,
                                      false,
                                    )
                                  : null}
                              </span>
                            )}
                          </CustomCell>
                        </Column>
                      ) : null}

                      {field.fieldName === t("ITEMS") ? (
                        <Column width={200} resizable key={index + "items"}>
                          <CustomHeaderCell>{t("ITEMS")}</CustomHeaderCell>
                          <OrderItemsCell
                            rowData={(rowData: any) => rowData}
                            positionFromRight="40%"
                          />
                        </Column>
                      ) : null}

                      {field.fieldName === t("AMOUNT") ? (
                        <Column width={80} resizable key={index + "amount"}>
                          <CustomHeaderCell>{t("AMOUNT")}</CustomHeaderCell>
                          <OrderAmountCell
                            rowData={(rowData: any) => rowData}
                          />
                        </Column>
                      ) : null}

                      {field.fieldName === t("TAX") ? (
                        <Column width={80} resizable key={index + "tax"}>
                          <CustomHeaderCell>{t("TAX")}</CustomHeaderCell>
                          <CustomCell dataKey="order_tax">
                            {(rowData: any) => (
                              <span>{displayField(rowData.order_tax)}</span>
                            )}
                          </CustomCell>
                        </Column>
                      ) : null}

                      {field.fieldName === t("ORDER_STATUS") ? (
                        <Column width={80} key={index + "order_status"}>
                          <CustomHeaderCell>
                            {t("ORDER_STATUS")}
                          </CustomHeaderCell>
                          <OrderAssignedServedCell
                            displayTitle="order_status"
                            rowData={(rowData: any) => rowData}
                          />
                        </Column>
                      ) : null}

                      {field.fieldName === t("NOTE") ? (
                        <Column width={140} resizable key={index + "note"}>
                          <CustomHeaderCell>{t("NOTE")}</CustomHeaderCell>
                          <OrderNoteCell rowData={(rowData: any) => rowData} />
                        </Column>
                      ) : null}

                      {field.fieldName === t("SERVED_BY") ? (
                        <Column width={140} resizable key={index + "served_by"}>
                          <CustomHeaderCell>{t("SERVED_BY")}</CustomHeaderCell>
                          <OrderAssignedServedCell
                            rowData={(rowData: any) => rowData}
                          />
                        </Column>
                      ) : null}

                      {field.fieldName === t("SERVICE_RATING") ? (
                        <Column width={60} key={index + "service_rating"}>
                          <CustomHeaderCell>
                            {t("SERVICE_RATING")}
                          </CustomHeaderCell>
                          <OrderServiceRatingCell
                            rowData={(rowData: any) => rowData}
                          />
                        </Column>
                      ) : null}

                      {field.fieldName === t("SERVICE_BY") ? (
                        <Column
                          width={140}
                          resizable
                          key={index + "service_by"}
                        >
                          <CustomHeaderCell>{t("SERVICE_BY")}</CustomHeaderCell>
                          <OrderAssignedServedCell
                            rowData={(rowData: any) => rowData}
                          />
                        </Column>
                      ) : null}

                      {field.fieldName === t("ORDER_SERVE_AT") ? (
                        <Column width={80} key={index + "order_serve_at"}>
                          <CustomHeaderCell>
                            {t("ORDER_SERVE_AT")}
                          </CustomHeaderCell>
                          <CustomCell dataKey="order_serve_at" />
                        </Column>
                      ) : null}

                      {field.fieldName === t("COMPANY") ? (
                        <Column width={120} resizable key={index + "company"}>
                          <CustomHeaderCell>{t("COMPANY")}</CustomHeaderCell>
                          <CustomCell dataKey="company_name" />
                        </Column>
                      ) : null}

                      {field.fieldName === t("STORE") ? (
                        <Column width={120} resizable key={index + "store"}>
                          <CustomHeaderCell>{t("STORE")}</CustomHeaderCell>
                          <CustomCell dataKey="store_name" />
                        </Column>
                      ) : null}
                    </>
                  );
                })}

              <Column width={isWebResolution ? 150 : 40} fixed="right">
                <CustomHeaderCell>{t("ACTION")}</CustomHeaderCell>
                <Cell>
                  {(rowData) => (
                    <OrderSingleAction
                      orderData={rowData}
                      onDeleteOrder={deleteOrder}
                      onCancelRestoreOrder={cancelRestoreOrder}
                      onServeOrder={serveOrder}
                      permission={permissionObj}
                    />
                  )}
                </Cell>
              </Column>
            </Table>
          </div>
        </Content>
        <Footer className="footer">
          <Pagination
            prev
            next
            first
            last
            ellipsis
            boundaryLinks
            maxButtons={5}
            size="xs"
            layout={["total", "-", "limit", "|", "pager", "skip"]}
            total={total}
            limitOptions={appConstObj.limit_options}
            limit={rows}
            activePage={page}
            onChangePage={onPageChange}
            onChangeLimit={handleChangeRows}
          />
        </Footer>
        <ToastContainer />
      </Container>
      <AlertModal
        open={openOrderValueModal}
        handleClose={(e: any) => {
          if (e == true) {
            setOpenOrderValueModal(false);
          } else {
            setOpenOrderValueModal(false);
          }
        }}
        displayText={t("ORDER_TOTAL_VALUE", {
          value: orderValue,
        })}
        modalTitle={t("ORDER_TOTAL_TITLE")}
      />
      <ImageModal
        ref={modalRef}
        modalTitle={t("PROFILE_PIC")}
        heading={t("PROFILE_PIC")}
        body={
          <img
            src={selectedOrder.customer_large_image_url}
            className="main-profile-pic"
            alt={t("PROFILE_PIC")}
          />
        }
        removePic={() => setOpenPictureDelModal(true)}
      />
    </div>
  );
}

export default OrdersTab;
